// frontend/src/pages/CandidatePortal.js
//
// Phase 5.5 — single-link candidate dashboard.
// The candidate is NOT a Django user. The token in the URL is the only
// credential. We pass it through the Authorization header on every fetch.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2, Circle, AlertTriangle, Upload, FileSignature, Eraser } from 'lucide-react';
import WorkPassApplicationForm from '../components/preonboarding/workpass/WorkPassApplicationForm';

const API_BASE = '/api/v1/candidate-portal';

const tokenHeader = (token) => ({
  Authorization: `Bearer ${token}`,
});

const CandidatePortal = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('t') || '';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [session, setSession] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const refreshSession = useCallback(async () => {
    if (!token) {
      setError('No access token in the URL.');
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/session/`, {
        headers: tokenHeader(token),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || 'Could not load your portal.');
      }
      setSession(await res.json());
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    refreshSession();
  }, [refreshSession]);

  const handleUpload = async (stepKey, documentType, file) => {
    setBusyKey(stepKey);
    setFeedback(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', documentType);
      formData.append('step_key', stepKey);
      const res = await fetch(`${API_BASE}/documents/`, {
        method: 'POST',
        headers: tokenHeader(token),
        body: formData,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || 'Upload failed.');
      }
      setFeedback({ type: 'success', message: 'File uploaded.' });
      refreshSession();
    } catch (err) {
      setFeedback({ type: 'error', message: err.message });
    } finally {
      setBusyKey(null);
    }
  };

  const handleDeclaration = async () => {
    setBusyKey('declaration_form_signed');
    setFeedback(null);
    try {
      const res = await fetch(`${API_BASE}/declaration/`, {
        method: 'POST',
        headers: { ...tokenHeader(token), 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || 'Failed.');
      }
      setFeedback({ type: 'success', message: 'Declaration recorded.' });
      refreshSession();
    } catch (err) {
      setFeedback({ type: 'error', message: err.message });
    } finally {
      setBusyKey(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="animate-spin text-indigo-600" size={36} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md bg-white rounded-2xl shadow p-8 text-center">
          <AlertTriangle className="mx-auto text-amber-500" size={36} />
          <h1 className="mt-4 text-lg font-semibold text-slate-800">
            Could not open your portal
          </h1>
          <p className="mt-2 text-sm text-slate-600">{error}</p>
          <p className="mt-4 text-xs text-slate-400">
            Please contact HR if you believe this is an error.
          </p>
        </div>
      </div>
    );
  }

  const { application, tasks = [] } = session || {};

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow p-8 mb-6">
          <h1 className="text-2xl font-semibold text-slate-800">
            Welcome, {application?.full_name}
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            Reference: <span className="font-mono">{application?.public_ref}</span>
          </p>
          <p className="mt-1 text-sm text-slate-600">
            Start date: <strong>{application?.offered_start_date || 'TBD'}</strong>
          </p>
        </div>

        {feedback && (
          <div
            className={`mb-6 p-4 rounded-lg text-sm ${
              feedback.type === 'success'
                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}
          >
            {feedback.message}
          </div>
        )}

        <div className="bg-white rounded-2xl shadow divide-y divide-slate-100">
          <div className="p-6">
            <h2 className="text-lg font-semibold text-slate-800">Pending tasks</h2>
            <p className="text-sm text-slate-500">
              Complete these before your start date.
            </p>
          </div>

          {tasks.length === 0 && (
            <div className="p-8 text-sm text-slate-500 text-center">
              You have no outstanding tasks. We will email you again if anything new comes up.
            </div>
          )}

          {tasks.map((task) => (
            <div key={task.step_key} className="p-6 flex items-start gap-4">
              <div className="mt-1">
                {task.completed ? (
                  <CheckCircle2 className="text-emerald-500" size={22} />
                ) : (
                  <Circle className="text-slate-300" size={22} />
                )}
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-800">{task.title}</h3>
                {task.description && (
                  <p className="mt-1 text-sm text-slate-600">{task.description}</p>
                )}

                {!task.completed && (
                  <div className="mt-3">
                    {task.step_key === 'declaration_form_signed' && (
                      <button
                        type="button"
                        onClick={handleDeclaration}
                        disabled={busyKey === task.step_key}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-50"
                      >
                        <FileSignature size={16} /> Acknowledge declaration
                      </button>
                    )}

                    {task.step_key === 'medical_form_uploaded' && (
                      <UploadButton
                        label="Upload medical certificate"
                        accept="application/pdf,image/*"
                        disabled={busyKey === task.step_key}
                        onSelect={(file) =>
                          handleUpload(task.step_key, 'medical_certificate', file)
                        }
                      />
                    )}

                    {task.step_key === 'nric_fin_uploaded' && (
                      <UploadButton
                        label="Upload NRIC / FIN scan"
                        accept="application/pdf,image/*"
                        disabled={busyKey === task.step_key}
                        onSelect={(file) =>
                          handleUpload(task.step_key, 'nric', file)
                        }
                      />
                    )}

                    {task.step_key === 'work_pass_form_completed' && (
                      <WorkPassFormTask
                        token={token}
                        onDone={(msg) => {
                          setFeedback({ type: 'success', message: msg });
                          refreshSession();
                        }}
                        onError={(msg) => setFeedback({ type: 'error', message: msg })}
                      />
                    )}

                    {task.step_key === 'loa_countersigned_by_candidate' && (
                      <SignaturePad
                        token={token}
                        disabled={busyKey === task.step_key}
                        onSigned={(msg) => {
                          setFeedback({ type: 'success', message: msg });
                          refreshSession();
                        }}
                        onError={(msg) => setFeedback({ type: 'error', message: msg })}
                        setBusy={(v) =>
                          setBusyKey(v ? task.step_key : null)
                        }
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <p className="mt-6 text-xs text-center text-slate-400">
          This link is unique to you and expires when no longer needed. Do not share it.
        </p>
      </div>
    </div>
  );
};

const UploadButton = ({ label, accept, onSelect, disabled }) => {
  const inputRef = React.useRef(null);
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onSelect(file);
          e.target.value = '';
        }}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={disabled}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-50"
      >
        <Upload size={16} /> {label}
      </button>
    </>
  );
};

const WorkPassFormTask = ({ token, onDone, onError }) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [values, setValues] = useState({});
  const [prefilledKeys, setPrefilledKeys] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/work-pass-form/`, { headers: tokenHeader(token) });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.detail || 'Could not load the form.');
        }
        const data = await res.json();
        if (!cancelled) {
          setValues(data.values || {});
          setPrefilledKeys(data.prefilled_keys || []);
        }
      } catch (err) {
        if (!cancelled) onError && onError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, onError]);

  const handleChange = useCallback((key, value) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSubmit = async (submitted) => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/work-pass-form/`, {
        method: 'POST',
        headers: { ...tokenHeader(token), 'Content-Type': 'application/json' },
        body: JSON.stringify({ values: submitted }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || 'Could not submit the form.');
      }
      onDone && onDone('Work pass application submitted. Thank you.');
    } catch (err) {
      onError && onError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-2">
      <WorkPassApplicationForm
        theme="employee"
        mode="candidate"
        loading={loading}
        submitting={submitting}
        values={values}
        prefilledKeys={prefilledKeys}
        onChange={handleChange}
        onSubmit={handleSubmit}
        submitLabel="Submit work pass application"
      />
    </div>
  );
};

const SignaturePad = ({ token, disabled, onSigned, onError, setBusy }) => {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef(null);
  const [hasStrokes, setHasStrokes] = useState(false);

  const getCtx = () => canvasRef.current?.getContext('2d');

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = canvas.offsetWidth * 2;
    canvas.height = canvas.offsetHeight * 2;
    const ctx = canvas.getContext('2d');
    ctx.scale(2, 2);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#0f172a';
  }, []);

  const pointFromEvent = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const handleStart = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastPointRef.current = pointFromEvent(e);
  };

  const handleMove = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = getCtx();
    const point = pointFromEvent(e);
    ctx.beginPath();
    ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
    lastPointRef.current = point;
    setHasStrokes(true);
  };

  const handleEnd = () => {
    drawingRef.current = false;
    lastPointRef.current = null;
  };

  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = getCtx();
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasStrokes(false);
  };

  const submit = async () => {
    if (!hasStrokes) {
      onError && onError('Please sign in the box before submitting.');
      return;
    }
    setBusy && setBusy(true);
    try {
      const dataUrl = canvasRef.current.toDataURL('image/png');
      const res = await fetch(`${API_BASE}/loa/sign/`, {
        method: 'POST',
        headers: { ...tokenHeader(token), 'Content-Type': 'application/json' },
        body: JSON.stringify({ canvas: dataUrl }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || 'Could not record signature.');
      }
      const body = await res.json();
      onSigned && onSigned(
        body.signed_pdf_hash
          ? 'Counter-signed. A signed copy has been stored.'
          : 'Counter-signature recorded.',
      );
      clear();
    } catch (err) {
      onError && onError(err.message);
    } finally {
      setBusy && setBusy(false);
    }
  };

  return (
    <div className="border border-slate-200 rounded-lg p-4 bg-slate-50 max-w-md">
      <p className="text-xs text-slate-500 mb-2">
        Sign inside the box below. Your IP, user-agent and signing time are
        recorded for the audit trail.
      </p>
      <canvas
        ref={canvasRef}
        onMouseDown={handleStart}
        onMouseMove={handleMove}
        onMouseUp={handleEnd}
        onMouseLeave={handleEnd}
        onTouchStart={handleStart}
        onTouchMove={handleMove}
        onTouchEnd={handleEnd}
        className="w-full h-32 bg-white border border-dashed border-slate-300 rounded touch-none"
      />
      <div className="flex gap-2 mt-3">
        <button
          type="button"
          onClick={clear}
          className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-white border border-slate-200 text-slate-600 text-sm hover:bg-slate-100"
        >
          <Eraser size={16} /> Clear
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={disabled || !hasStrokes}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-50"
        >
          <FileSignature size={16} /> Counter-sign LOA
        </button>
      </div>
    </div>
  );
};

export default CandidatePortal;
